import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Slot } from "@radix-ui/react-slot";
import { PanelLeft } from "lucide-react";
import { cn } from "@/shared/lib/cn.js";
import { Button } from "./button.jsx";
import { Sheet, SheetContent, SheetTitle } from "./sheet.jsx";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip.jsx";
import { useMediaQuery } from "@/shared/hooks/use-media-query.js";

/**
 * Barra lateral do sistema.
 *
 * Reescrita enxuta do componente equivalente do app antigo (que tinha mais
 * de 700 linhas e vários recursos nunca usados). Aqui existem só os três
 * comportamentos que as telas realmente pedem:
 *   1. recolher para o modo ícone no desktop;
 *   2. virar painel deslizante no celular;
 *   3. mostrar tooltip com o nome quando está recolhida.
 *
 * O estado "recolhida" fica no localStorage — é preferência visual, não
 * dado de negócio.
 */

const SidebarContext = createContext(null);
const CHAVE_PREFERENCIA = "odontoplug:sidebar:collapsed";

export function SidebarProvider({ children, defaultCollapsed = false }) {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [mobileOpen, setMobileOpen] = useState(false);

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return defaultCollapsed;
    return window.localStorage.getItem(CHAVE_PREFERENCIA) === "true";
  });

  useEffect(() => {
    window.localStorage.setItem(CHAVE_PREFERENCIA, String(collapsed));
  }, [collapsed]);

  const valor = useMemo(
    () => ({
      isMobile,
      collapsed: isMobile ? false : collapsed,
      mobileOpen,
      setMobileOpen,
      toggle: () => (isMobile ? setMobileOpen((v) => !v) : setCollapsed((v) => !v)),
    }),
    [isMobile, collapsed, mobileOpen],
  );

  return (
    <SidebarContext.Provider value={valor}>
      <TooltipProvider delayDuration={0}>{children}</TooltipProvider>
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const contexto = useContext(SidebarContext);
  if (!contexto) throw new Error("useSidebar precisa estar dentro de <SidebarProvider>.");
  return contexto;
}

// ------------------------------------------------------------------ estrutura

export function Sidebar({ className, children }) {
  const { isMobile, collapsed, mobileOpen, setMobileOpen } = useSidebar();

  const conteudo = (
    <div
      data-collapsed={collapsed}
      className={cn("group/sidebar flex h-full flex-col bg-sidebar text-sidebar-foreground", className)}
    >
      {children}
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-64 p-0" hideClose>
          <SheetTitle className="sr-only">Menu de navegação</SheetTitle>
          {conteudo}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside
      className={cn(
        "sticky top-0 hidden h-screen shrink-0 border-r border-sidebar-border transition-[width] duration-200 md:block",
        collapsed ? "w-14" : "w-64",
      )}
    >
      {conteudo}
    </aside>
  );
}

export function SidebarTrigger({ className }) {
  const { toggle } = useSidebar();
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      aria-label="Alternar menu lateral"
      className={cn("h-8 w-8", className)}
    >
      <PanelLeft className="h-4 w-4" />
    </Button>
  );
}

export function SidebarHeader({ className, ...props }) {
  return <div className={cn("border-b border-sidebar-border/60 p-2", className)} {...props} />;
}

export function SidebarContent({ className, ...props }) {
  return (
    <div className={cn("scrollbar-slim flex-1 overflow-y-auto overflow-x-hidden py-2", className)} {...props} />
  );
}

export function SidebarFooter({ className, ...props }) {
  return <div className={cn("border-t border-sidebar-border/60 p-2", className)} {...props} />;
}

export function SidebarGroup({ className, ...props }) {
  return <div className={cn("px-2 py-1", className)} {...props} />;
}

export function SidebarGroupLabel({ className, ...props }) {
  const { collapsed } = useSidebar();
  if (collapsed) return <div className="my-2 h-px bg-sidebar-border/60" />;
  return (
    <div
      className={cn(
        "px-2 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-sidebar-foreground/50",
        className,
      )}
      {...props}
    />
  );
}

export function SidebarMenu({ className, ...props }) {
  return <ul className={cn("flex flex-col gap-0.5", className)} {...props} />;
}

export function SidebarMenuItem({ className, ...props }) {
  return <li className={cn("relative", className)} {...props} />;
}

/**
 * Item clicável do menu.
 * @param {object} props
 * @param {boolean} [props.asChild]  para envolver um <Link> do router
 * @param {boolean} [props.isActive] destaca a rota atual
 * @param {string}  [props.tooltip]  texto mostrado quando a barra está recolhida
 */
export function SidebarMenuButton({
  asChild = false,
  isActive = false,
  tooltip,
  className,
  children,
  ...props
}) {
  const { collapsed } = useSidebar();
  const Comp = asChild ? Slot : "button";

  const botao = (
    <Comp
      data-active={isActive}
      className={cn(
        "flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-sm outline-none transition-colors",
        "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        "focus-visible:ring-2 focus-visible:ring-sidebar-ring",
        "[&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0",
        isActive && "bg-sidebar-accent font-medium text-sidebar-accent-foreground",
        collapsed && "justify-center px-0",
        // Esconde o rótulo textual quando recolhida, mantendo o ícone.
        collapsed && "[&>span]:hidden",
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  );

  if (!collapsed || !tooltip) return botao;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{botao}</TooltipTrigger>
      <TooltipContent side="right">{tooltip}</TooltipContent>
    </Tooltip>
  );
}

/** Some por completo quando a barra está recolhida (blocos de perfil, logo). */
export function SidebarHideWhenCollapsed({ children, className }) {
  const { collapsed } = useSidebar();
  if (collapsed) return null;
  return <div className={className}>{children}</div>;
}
